/**
 * The 'mono' theme — Swiss / International Typographic Style.
 *
 * The systematic "design-system" world: a neutral grotesque, a near-monochrome
 * ink-on-paper palette with ONE restrained signal-red accent, ZERO radius,
 * hairline rules, TIGHT negative tracking on display type, wide tracking on
 * uppercase labels, a small grid space-unit, and a deliberately MODEST hero
 * stat (Swiss restraint — the number is set, not blown up). This is the coldest,
 * most brutalist-precise theme in the catalog: where Xavior is a friendly
 * blue-square voice with soft 12px corners and generous spacing, Mono is a
 * gridded specimen sheet — flat surfaces, hard right-angles, tabular figures.
 *
 * This is a COMPLETE theme (parity with `xavior.ts`): every required
 * ArtTokenMap key, every §2.6 layout token set to DISTINCT Swiss values, tokens
 * for all 6 required treatments + the 4 optional treatments, a real Google
 * Fonts <link>, and a `layoutCss` block carrying the per-treatment geometry that
 * tokens alone can't express (the eyebrow's numbered grid marker, the stat's
 * accent square + baseline rule, the callout's flat left bar, the quote's hard
 * top rule). The token-only path still renders coherently without it.
 *
 * Authority: docs/architecture/studio-token-contract.md (§2 tokens, §2.6 layout,
 * §3 treatments, §5.2 StudioTheme). Reference completeness: themes/xavior.ts.
 */
/**
 * Palette — near-monochrome neutral grey ramp on warm-neutral paper, plus ONE
 * accent. The ink is a true near-black (not pure #000 — softened to #111 so
 * hairlines read crisp without harshness). The accent is a single restrained
 * signal red (`#e5341c`) — used ONLY for the accent square, the inline emphasis,
 * the eyebrow rule, and link/marker bars; never as a field. Trend up/down use
 * the SAME neutral discipline: a muted green and the signal red itself, so the
 * deck never gains a second loud hue.
 */
const PAPER = '#fcfcfb'; //                page canvas — barely-warm off-white
const PAPER_SURFACE = '#f4f4f2'; //        raised panels / table header band
const INK = '#111111'; //                  primary ink (near-black grotesque)
const INK_INVERSE_FIELD = '#111111'; //    the dark slide canvas (ink-as-field)
const INK_SECONDARY = '#3f3f3f'; //        subheads, captions
const INK_MUTED = '#8a8a87'; //            labels, eyebrows base, footnotes
const ACCENT = '#e5341c'; //               the single signal-red accent
const ACCENT_DIM = 'rgba(229, 52, 28, 0.08)'; // faint accent wash
const BORDER = '#dededb'; //               hairline dividers / cell borders
const BORDER_STRONG = '#111111'; //        the emphasis rule IS ink (Swiss: 1px black)
const TREND_UP = '#1f6f3d'; //             muted, systematic green (not loud)
/** The neutral grotesque stack. Helvetica Neue / Neue Haas grotesk lineage,
 *  with Inter as the loadable web fallback (tracked tight to read as Helvetica).
 *  Body and display share ONE family — the Swiss discipline of a single
 *  typeface, differentiated by weight/size/tracking, not by family contrast. */
const GROTESQUE = "'Inter', 'Helvetica Neue', 'Neue Haas Grotesk Display Pro', Helvetica, Arial, sans-serif";
/** Tabular figures for the hero stat + numbered eyebrow markers. */
const MONO = "'IBM Plex Mono', 'SFMono-Regular', ui-monospace, Menlo, monospace";
export const mono = {
    id: 'mono',
    label: 'Mono',
    layoutLanguage: 'systematic',
    fontsUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap',
    tokens: {
        // ── 2.1 typography ──
        // One grotesque for everything; weight + tracking do the work, not family.
        '--xv-art-font-display': GROTESQUE,
        '--xv-art-font-body': GROTESQUE,
        '--xv-art-font-mono': MONO,
        // Display sits at 700 — assertive but not the 800 of a keynote shout.
        '--xv-art-font-weight-display': '700',
        '--xv-art-font-weight-body': '400',
        // TIGHT negative tracking on display (Helvetica set tight is the Swiss tell).
        '--xv-art-tracking-display': '-0.035em',
        // WIDE tracking on the uppercase eyebrow/label — the gridded specimen look.
        '--xv-art-tracking-eyebrow': '0.22em',
        // ── 2.2 surface ──
        '--xv-art-bg': PAPER,
        '--xv-art-bg-surface': PAPER_SURFACE,
        // The dark slide IS the ink — a flat near-black field, no blue cast.
        '--xv-art-bg-inverse': INK_INVERSE_FIELD,
        '--xv-art-surface-hover': '#ededea',
        // ── 2.3 ink ──
        '--xv-art-ink': INK,
        '--xv-art-ink-secondary': INK_SECONDARY,
        '--xv-art-ink-muted': INK_MUTED,
        // On the dark field, text drops to the warm paper (not pure white — paper).
        '--xv-art-ink-inverse': PAPER,
        // ── 2.4 accent ──
        '--xv-art-accent': ACCENT,
        // White glyph on the signal-red square.
        '--xv-art-accent-ink': '#ffffff',
        '--xv-art-accent-dim': ACCENT_DIM,
        // Charts get a second series color = ink (mono-discipline: red + black only).
        '--xv-art-accent-2': INK,
        // ── 2.5 borders ──
        // Hairline grey for the field grid; the strong rule is a full 1px BLACK.
        '--xv-art-border': BORDER,
        '--xv-art-border-strong': BORDER_STRONG,
        // ── 2.6 layout — DISTINCT Swiss values (the structural identity) ──
        // Tighter measure than Xavior's 780 — a denser, more set column.
        '--xv-art-measure': '680px',
        // SMALL base unit (6px) — Mono is the tightest grid in the catalog
        // (Xavior 8, Boardroom 12). All block padding/gaps derive from this.
        '--xv-art-space-unit': '6px',
        // A precise, modular gutter — multiple of the unit (6 × 9 = 54px).
        '--xv-art-gutter': '54px',
        // ZERO radius — the defining Mono token. Hard right angles everywhere.
        '--xv-art-radius': '0',
        // Small radius ALSO zero (no half-radius derivation — nothing rounds).
        '--xv-art-radius-sm': '0',
        // TIGHT body leading (1.45) — denser than Xavior 1.6, far from Editorial 1.75.
        '--xv-art-line-height': '1.45',
        // Display nearly solid-set (0.98) — slabs of type, minimal interline air.
        '--xv-art-line-height-display': '0.98',
        // ── 2.7 stat primitives ──
        // Stat ink = ink (the number is set, not colored). Tabular numerals.
        // Tracks the resolved ink (#5339) so a brand kit's ink reaches the figure.
        '--xv-art-stat-ink': `var(--xv-art-ink, ${INK})`,
        '--xv-art-stat-positive': TREND_UP,
        '--xv-art-stat-negative': ACCENT,
        // MODEST hero stat — Swiss restraint. Big enough to lead, far from the
        // 6rem Xavior shout. clamp keeps it controlled across deck/doc/email.
        '--xv-art-stat-size': 'clamp(2.5rem, 5.5vw, 4rem)',
        // ── 3.1 highlight — WEIGHT-ONLY (no color, no marker) ──
        // The Swiss emphasis: a heavier cut of the same grotesque. "3.1×" goes bold,
        // never colored — color is reserved for the accent square + rules.
        '--xv-art-highlight-ink': 'inherit',
        '--xv-art-highlight-bg': 'transparent',
        '--xv-art-highlight-weight': '700',
        '--xv-art-highlight-pad': '0',
        // ── 3.2 callout — flat panel, hairline border, hard left bar ──
        '--xv-art-callout-bg': PAPER_SURFACE,
        '--xv-art-callout-border': BORDER,
        // Callout body ink tracks the resolved ink (#5339).
        '--xv-art-callout-ink': `var(--xv-art-ink, ${INK})`,
        // Zero radius — flat box, square corners.
        '--xv-art-callout-radius': '0',
        // Insight bar = the signal accent; warning/success stay disciplined.
        // Tracks the resolved accent so a brand kit's accent flows through (#5319);
        // renders byte-identical signal red without a kit.
        '--xv-art-callout-accent-insight': `var(--xv-art-accent, ${ACCENT})`,
        '--xv-art-callout-accent-warning': '#c47b00', // muted ochre, not the loud #f59e0b
        '--xv-art-callout-accent-success': TREND_UP,
        // ── 3.3 stat ──
        // Stat labels track the resolved muted ink (#5352).
        '--xv-art-stat-label-ink': `var(--xv-art-ink-muted, ${INK_MUTED})`,
        // WIDE label tracking — the uppercase band labels ("NET RETENTION") set wide.
        '--xv-art-stat-label-tracking': '0.14em',
        // Numerals in mono → tabular, aligned, machined.
        '--xv-art-stat-numeral-font': MONO,
        // ── 3.4 quote — grotesque (NOT serif), upright, sized down ──
        // The anti-Editorial pull quote: same neutral grotesque, NOT italic, a hard
        // accent top-rule instead of a curly mark (geometry in layoutCss). Set in
        // the FULL ink (Swiss: no romantic dimming) — tracks the resolved ink (#5339).
        '--xv-art-quote-ink': `var(--xv-art-ink, ${INK})`,
        '--xv-art-quote-bar': `var(--xv-art-accent, ${ACCENT})`,
        '--xv-art-quote-font': GROTESQUE,
        '--xv-art-quote-size': '1.45rem',
        '--xv-art-quote-style': 'normal',
        // ── 3.5 eyebrow — uppercase, wide-tracked, accent rule/number marker ──
        // The eyebrow kicker tracks the resolved muted ink (#5352).
        '--xv-art-eyebrow-ink': `var(--xv-art-ink-muted, ${INK_MUTED})`,
        '--xv-art-eyebrow-tracking': '0.22em',
        '--xv-art-eyebrow-size': '0.6875rem',
        '--xv-art-eyebrow-transform': 'uppercase',
        '--xv-art-eyebrow-rule': `var(--xv-art-accent, ${ACCENT})`,
        // ── 3.6 wordmark — uppercase, tight-tracked, grotesque ──
        // Deliberately FULL ink (not muted — Swiss masthead); tracks the resolved
        // ink (#5339).
        '--xv-art-wordmark-ink': `var(--xv-art-ink, ${INK})`,
        '--xv-art-wordmark-font': GROTESQUE,
        '--xv-art-wordmark-size': '0.75rem',
        '--xv-art-wordmark-weight': '700',
        // ── 3.7 optional treatments ──
        // Link = accent, underlined (geometry refines the underline in layoutCss).
        '--xv-art-link-ink': `var(--xv-art-accent, ${ACCENT})`,
        // Code in the mono face on a flat grey chip, square corners. Code ink
        // tracks the resolved ink (#5339).
        '--xv-art-code-bg': PAPER_SURFACE,
        '--xv-art-code-ink': `var(--xv-art-ink, ${INK})`,
        // ── 4.2 logo slot (theme leaves logo empty → text wordmark renders) ──
        '--xv-art-logo-url': 'none',
        '--xv-art-logo-aspect': 'auto',
        '--xv-art-logo-height': '0.75rem',
        '--xv-art-logo-invert': '0',
    },
    /**
     * Richer per-treatment geometry the token set can't express. Scoped under
     * [data-xv-theme="mono"] by the renderer. The token-only path above already
     * renders a coherent Mono; this sharpens it into the Swiss specimen-sheet look:
     *
     *  - eyebrow: the index number ("04") becomes a fixed-width mono marker with a
     *    short hard accent tick before the label — the gridded section header.
     *  - stat: the accent square is set as a true square sized to the cap-height,
     *    and the value sits on a 1px black baseline rule (the band divider IS the
     *    grid line, not a soft border).
     *  - stats-band: separated by a vertical hairline, labels set wide & uppercase.
     *  - callout: zero-radius, a flat (not rounded) left bar at exactly the unit
     *    width, tight inner padding off the small space-unit.
     *  - quote: NO curly mark — a hard full-width accent rule above, upright
     *    grotesque, tight leading. The anti-romantic pull quote.
     *  - link: a thin offset underline that thickens to the accent on hover.
     *  - wordmark: uppercase, wide-tracked, with a leading accent tick.
     *
     * All sizing derives from --xv-art-space-unit (6px) so the grid stays modular.
     */
    layoutCss: `
[data-xv-theme="mono"] {
  /* Hard-set the family even where a block forgot a token — Swiss is unforgiving. */
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

/* ── EYEBROW: numbered grid marker + hard accent tick ── */
[data-xv-theme="mono"] .xv-art-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--xv-art-space-unit) * 1.5);
  font-variant-numeric: tabular-nums;
}
[data-xv-theme="mono"] .xv-art-eyebrow::before {
  content: "";
  display: inline-block;
  width: calc(var(--xv-art-space-unit) * 3);
  height: 2px;
  background: var(--xv-art-eyebrow-rule);
  flex: 0 0 auto;
}
[data-xv-theme="mono"] .xv-art-eyebrow__index {
  font-family: var(--xv-art-font-mono);
  font-weight: 500;
  color: var(--xv-art-ink);
  letter-spacing: 0;
}

/* ── STAT: cap-height accent square + 1px black baseline rule ── */
[data-xv-theme="mono"] .xv-art-stat {
  font-family: var(--xv-art-stat-numeral-font);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 0.9;
  display: flex;
  align-items: baseline;
  gap: calc(var(--xv-art-space-unit) * 2.5);
  padding-bottom: calc(var(--xv-art-space-unit) * 2);
  border-bottom: 1px solid var(--xv-art-border-strong);
}
[data-xv-theme="mono"] .xv-art-stat__accent-square,
[data-xv-theme="mono"] .xv-art-stat .accent-square {
  width: 0.62em;
  height: 0.62em;
  background: var(--xv-art-accent);
  border-radius: 0;
  align-self: center;
  flex: 0 0 auto;
}
[data-xv-theme="mono"] .xv-art-stat__trend {
  font-family: var(--xv-art-font-body);
  font-size: 0.32em;
  font-weight: 600;
  letter-spacing: 0;
  align-self: center;
}
[data-xv-theme="mono"] .xv-art-stat__label {
  font-family: var(--xv-art-font-body);
  text-transform: uppercase;
  letter-spacing: var(--xv-art-stat-label-tracking);
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--xv-art-stat-label-ink);
}

/* ── STATS-BAND: vertical hairline between figures, gridded ── */
[data-xv-theme="mono"] .xv-art-stats-band {
  display: grid;
  grid-auto-flow: column;
  gap: calc(var(--xv-art-space-unit) * 6);
  border-top: 1px solid var(--xv-art-border-strong);
  padding-top: calc(var(--xv-art-space-unit) * 3);
}
[data-xv-theme="mono"] .xv-art-stats-band > * + * {
  border-left: 1px solid var(--xv-art-border);
  padding-left: calc(var(--xv-art-space-unit) * 6);
}

/* ── HIGHLIGHT: weight-only, never colored ── */
[data-xv-theme="mono"] .xv-art-highlight {
  font-weight: var(--xv-art-highlight-weight);
  color: inherit;
  background: transparent;
}

/* ── CALLOUT: flat box, square corners, hard unit-width left bar ── */
[data-xv-theme="mono"] .xv-art-callout {
  border-radius: 0;
  border: 1px solid var(--xv-art-callout-border);
  border-left: var(--xv-art-space-unit) solid var(--xv-art-callout-accent-insight);
  background: var(--xv-art-callout-bg);
  padding: calc(var(--xv-art-space-unit) * 3) calc(var(--xv-art-space-unit) * 4);
}
[data-xv-theme="mono"] .xv-art-callout--warning {
  border-left-color: var(--xv-art-callout-accent-warning);
}
[data-xv-theme="mono"] .xv-art-callout--success {
  border-left-color: var(--xv-art-callout-accent-success);
}
[data-xv-theme="mono"] .xv-art-callout__heading {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--xv-art-ink-muted);
  margin-bottom: var(--xv-art-space-unit);
}

/* ── QUOTE: hard accent top-rule, upright grotesque, NO curly mark ── */
[data-xv-theme="mono"] .xv-art-quote {
  font-family: var(--xv-art-quote-font);
  font-style: normal;
  font-weight: 500;
  color: var(--xv-art-quote-ink);
  font-size: var(--xv-art-quote-size);
  line-height: 1.25;
  letter-spacing: -0.01em;
  border-left: 0;
  padding: calc(var(--xv-art-space-unit) * 3) 0 0 0;
  margin: calc(var(--xv-art-space-unit) * 4) 0;
  border-top: 2px solid var(--xv-art-quote-bar);
}
[data-xv-theme="mono"] .xv-art-quote::before {
  content: none;
}
[data-xv-theme="mono"] .xv-art-quote__attribution {
  display: block;
  margin-top: calc(var(--xv-art-space-unit) * 2);
  font-family: var(--xv-art-font-body);
  font-style: normal;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--xv-art-ink-muted);
}

/* ── LINK: thin offset underline, thickens to accent on hover ── */
[data-xv-theme="mono"] .xv-art-link {
  color: var(--xv-art-link-ink);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
}
[data-xv-theme="mono"] .xv-art-link:hover {
  text-decoration-thickness: 2px;
}

/* ── CODE: square mono chip ── */
[data-xv-theme="mono"] .xv-art-code {
  font-family: var(--xv-art-font-mono);
  background: var(--xv-art-code-bg);
  color: var(--xv-art-code-ink);
  border: 1px solid var(--xv-art-border);
  border-radius: 0;
  padding: 0.1em 0.4em;
  font-size: 0.85em;
}

/* ── WORDMARK: uppercase, wide-tracked, leading accent tick ── */
[data-xv-theme="mono"] .xv-art-wordmark {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-weight: var(--xv-art-wordmark-weight);
  color: var(--xv-art-wordmark-ink);
  display: inline-flex;
  align-items: center;
  gap: calc(var(--xv-art-space-unit) * 1.5);
}
[data-xv-theme="mono"] .xv-art-wordmark::before {
  content: "";
  display: inline-block;
  width: calc(var(--xv-art-space-unit) * 2);
  height: calc(var(--xv-art-space-unit) * 2);
  background: var(--xv-art-accent);
  border-radius: 0;
  flex: 0 0 auto;
}

/* ── DIVIDER: a single hard black hairline (no dot-leader) ── */
[data-xv-theme="mono"] .xv-art-divider {
  border: 0;
  border-top: 1px solid var(--xv-art-border-strong);
  margin: calc(var(--xv-art-space-unit) * 4) 0;
}

/* ── CHIP: square outline tag, uppercase ── */
[data-xv-theme="mono"] .xv-art-chip {
  background: transparent;
  color: var(--xv-art-ink);
  border: 1px solid var(--xv-art-border-strong);
  border-radius: 0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0.15em 0.5em;
}

/* ── DARK / INVERSE slide: ink field, paper text, hairlines invert ── */
[data-xv-theme="mono"] .xv-art--inverse,
[data-xv-theme="mono"] [data-inverse="true"] {
  background: var(--xv-art-bg-inverse);
  color: var(--xv-art-ink-inverse);
}
[data-xv-theme="mono"] .xv-art--inverse .xv-art-stat,
[data-xv-theme="mono"] .xv-art--inverse .xv-art-stats-band {
  border-color: rgba(252, 252, 251, 0.28);
}
`,
};
//# sourceMappingURL=mono.js.map